import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { chosenViewBackground, chosenViewColor, chosenViewShadow } from '@components/calendar/header/ViewButton';

import { getId } from '@state/semester/selectors';
import MaterialIcon from '@components/common/icons/MaterialIcon';
import Tooltip from '@components/common/tooltip/Tooltip';
import history from '@utils/history';
import { SIDEBAR_VIEW_SWITCH_ATTRIBUTE, VIEW_SWITCH_ICON_CLASS } from '@utils/viewIconsFlight';
import { goToHomeLink, goToTasksLink } from '../navigationHelpers';
import t from '../../../i18n/t';

type Props = {
  /** The narrow icon column's version (ColumnViews): the two icons stacked, their names on hover. */
  vertical?: boolean;
  /** The sidebar dragged to its narrowest: the names only, the icons don't fit beside them. */
  narrow?: boolean;
};

/** How tall the narrow icon column's version stands. */
export const COLUMN_SWITCH_HEIGHT = 68;

/**
 * Calendar | Task List, moved out of the top bar into the top of the left sidebar, the same place on both pages, so the
 * bar has room for the timer (top-bar tidy, Petr, 2026-10-31). The chosen half is a raised card on a grey track, like
 * Claude's Chat / Code. With the sidebar closed, a stacked version stands in the narrow icon column (ColumnViews).
 */
const ViewSwitch: FC<Props> = ({ vertical, narrow }) => {
  const semesterId = useSelector(getId);
  const { pathname } = useLocation();
  if (!semesterId) return null;

  const views = [
    // A calendar with one event on it: the grid of dots (calendar_month) was busy at this size (Petr, 2026-10-31).
    { key: 'calendar', icon: 'event', label: t.CALENDAR, link: goToHomeLink(semesterId) },
    { key: 'tasks', icon: 'checklist', label: t.TASK_LIST, link: goToTasksLink(semesterId) }
  ];

  return (
    <Track $vertical={vertical} {...(vertical ? {} : { [SIDEBAR_VIEW_SWITCH_ATTRIBUTE]: true })} role="tablist">
      {views.map(view => {
        const selected = pathname === view.link;
        const half = (
          <Half
            key={view.key}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-label={vertical ? view.label : undefined}
            $selected={selected}
            onClick={() => !selected && history.push(view.link)}
          >
            {(vertical || !narrow) && (
              <MaterialIcon
                className={VIEW_SWITCH_ICON_CLASS}
                name={view.icon}
                size={vertical ? 20 : 16}
                color="currentColor"
              />
            )}
            {!vertical && view.label}
          </Half>
        );
        return vertical ? (
          <Tooltip key={view.key} target={half} content={view.label} position="right center" width="max-content" />
        ) : (
          half
        );
      })}
    </Track>
  );
};

export default ViewSwitch;

// A pale track, the chosen view's card on it with just a hint of shadow: the darker track and fuller shadow made the
// switches heavy (Petr, 2026-10-31). The calendar bar's Week / Month switch looks the same (ViewButton).
const Track = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex-direction: ${props => (props.$vertical ? 'column' : 'row')};
  gap: 4px;
  ${props =>
    props.$vertical
      ? // Cards as tall as the sidebar switch's. Wider than the column's 32px inner room, so it reaches 5px past it on
        // each side to stay centred over the icons below.
        `height: ${COLUMN_SWITCH_HEIGHT}px; width: 42px; margin: 10px -5px 0;`
      : // Level with the top bar's buttons beside it, a few pixels in from the sidebar's sides (Petr, 2026-10-31).
        `height: 36px; margin: 0 14px 6px;`}
  padding: 4px;
  box-sizing: border-box;
  border-radius: 10px;
  background: color-mix(
    in srgb,
    ${props => props.theme.textStrongColor} ${props => (props.theme.name === 'dark' ? 6 : 4)}%,
    transparent
  );
  flex-shrink: 0;
`;

const Half = styled.button<{ $selected: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 0;
  padding: 0 6px;
  border: 0;
  border-radius: 7px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  cursor: ${props => (props.$selected ? 'default' : 'pointer')};
  // The chosen half as the calendar bar's chosen view: a white card with purple words, on dark a soft purple one.
  color: ${props => (props.$selected ? chosenViewColor(props.theme) : props.theme.textColor)};
  background: ${props => (props.$selected ? chosenViewBackground(props.theme) : 'transparent')};
  box-shadow: ${props => (props.$selected ? chosenViewShadow(props.theme) : 'none')};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    ${props =>
      !props.$selected &&
      `color: ${props.theme.primary};
       background: color-mix(in srgb, ${props.theme.primary} 12%, transparent);`}
  }
`;
